import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import BigBookingWidgetHeader from './BigBookingWidgetHeader';
import { BookingWidgetMap } from 'components/bookingWidgetMap';
import { CancellationAndPaymentPolicyModal } from 'components/cancellationAndPaymentPolicyModal';
import { ContactUsModal } from 'components/contactUsModal';
import * as constants from './../../constants';
import { Click2CallModal } from 'components/click2CallModal';
import { ShareHotelModal } from 'components/shareHotelModal';
import { Drawer } from '../common/drawer';
import { Multiproperty } from 'features/multiproperty';
import { RoomDetails } from '../roomDetails';
import { PropertyDetails } from 'features/MiniBookingWidget/propertyDetails';
import { SidebarMenu } from '../sidebarMenu';
import { TrackPricesModal } from 'components/trackPricesModal';
import { useGetBookingData } from 'hooks';

import { closeDrawer, openDrawer } from '../../features/drawersSlice';

import { setSelectedRoom } from '../../features/roomsSlice';
import { setSelectedPropertyId } from 'features/multiproperty/MultipropertySlice';
import './BigBookingWidgetDrawer.css';
import './Styles.css'; // TODO: THIS IS JUST TEMPORARY (I HOPE SO)
import { BookingWidgetForm } from 'components/bookingWidgetForm';
import { TermsAndConditionsModal } from 'components/termsAndConditionsModal';
import { OfferModal } from 'components/offerModal';
import { CartForm } from 'components/cartForm';
import { AreYouSureModal } from 'components/areYouSureModal';
import { ReviewsModal } from 'components/reviewsModal';
import { AmenitiesModal } from 'components/amenitiesModal';

const BigBookingWidgetDrawer = ({ anchor, propertyType, widgetVariant }) => {
    const { getBookingData } = useGetBookingData();
    const dispatch = useDispatch();

    const selectedPropertyId = useSelector(
        (state) => state.multiproperty?.selectedPropertyId
    );

    const multipropertyPropeties =
        useSelector((state) => state.multiproperty.multipropertyProperties) ||
        [];

    const markers = useSelector((state) => state.multiproperty.markers) || [];

    const selectedProperty = multipropertyPropeties.find((multiproperty) => {
        return multiproperty.propertyID === selectedPropertyId;
    });

    const data = useSelector((state) => state.miniBookingWidget?.data);
    const sortByClick2Call = data?.click2CallDropDowns;

    const isOpen = useSelector(
        (state) => state.drawers.isBigBookingWidgetDrawerOpen
    );

    const showOffer = useSelector(
        (state) => state.miniBookingWidget?.data?.enableWidgetOffers
    );

    const isMapOpen = useSelector((state) => state.drawers.isMapDrawerOpen);

    const showMap = useCallback(() => {
        dispatch(openDrawer({ drawerId: 'MapDrawer' }));
    }, []);

    const closeMap = useCallback(() => {
        dispatch(closeDrawer({ drawerId: 'MapDrawer' }));
    }, []);

    const menuItems = useSelector(
        (state) => state.miniBookingWidget?.data?.pluginMenus
    );

    const {
        isTrackPricesModalOpen,
        isCancellationAndPaymentPolicyModalOpen,
        isContactUsModalOpen,
        isReviewsModalOpen,
        isOfferModalOpen,
        isClick2CallModalOpen,
        isShareHotelModalOpen,
        isTermsAndConditionsModalOpen,
        isAreYouSureModalOpen,
        isAmenitiesModalOpen,
    } = useSelector((state) => state.modal);

    const selectedRoomIndex = useSelector(
        (state) => state.rooms.selectedRoomIndex
    );

    const selectedRoomFormIndex = useSelector(
        (state) => state.roomForm.selectedRoomFormIndex
    );

    const isCartOpen = useSelector((state) => state.cart?.open);

    // TODO: DON'T KNOW IF THIS IS IDEAL, MAYBE USE ENVIRONMENT VARIABLE
    const mapboxAccessToken = useSelector(
        (state) => state.miniBookingWidget?.data?.mapboxAccessToken
    );
    const enableMap =
        useSelector((state) => state.miniBookingWidget?.data?.showMap) &&
        Boolean(mapboxAccessToken);

    const handleClickOutside = useCallback(() => {
        // If track prices modal is open do not close drawer
        if (
            isTrackPricesModalOpen ||
            isCancellationAndPaymentPolicyModalOpen ||
            isContactUsModalOpen ||
            isReviewsModalOpen ||
            isClick2CallModalOpen ||
            isShareHotelModalOpen ||
            isTermsAndConditionsModalOpen ||
            isOfferModalOpen ||
            isAreYouSureModalOpen ||
            isAmenitiesModalOpen
        ) {
            return;
        }

        setTimeout(() => {
            if (widgetVariant !== 'openBigDrawer') {
                dispatch(closeDrawer({ drawerId: 'BigBookingWidgetDrawer' }));
            }

            dispatch(setSelectedRoom({ selectedRoomIndex: null }));
            dispatch(closeDrawer({ drawerId: 'MapDrawer' }));
        }, 0);
    }, [
        dispatch,
        isTrackPricesModalOpen,
        isCancellationAndPaymentPolicyModalOpen,
        isContactUsModalOpen,
        isReviewsModalOpen,
        isOfferModalOpen,
        isClick2CallModalOpen,
        isShareHotelModalOpen,
        isTermsAndConditionsModalOpen,
        isAreYouSureModalOpen,
        isAmenitiesModalOpen,
    ]);

    const handleSidebarMenuBtnClick = useCallback(() => {
        dispatch(closeDrawer({ drawerId: 'BigBookingWidgetDrawer' }));
    }, [dispatch]);

    const closeProperyDetails = useCallback(() => {
        dispatch(setSelectedPropertyId({ selectedPropertyId: null }));
        getBookingData();
    }, [setSelectedPropertyId, getBookingData]);

    const openPropertyDetails = useCallback(
        (propertyId, parentPropertyId) => {
            dispatch(setSelectedPropertyId({ selectedPropertyId: propertyId }));
            getBookingData({
                propertyId,
                parentPropertyId,
            });
        },
        [setSelectedPropertyId, getBookingData]
    );

    return (
        <div>
            <Drawer
                anchor={anchor}
                open={isOpen || widgetVariant === 'openBigDrawer'}
                className={`ab-big-booking-widget-drawer`}
                onClose={handleClickOutside}
                widgetVariant={widgetVariant}
            >
                <>
                    {widgetVariant !== 'openBigDrawer' && (
                        <SidebarMenu
                            menuItems={menuItems}
                            onClick={handleSidebarMenuBtnClick}
                        />
                    )}

                    <div
                        className={`ab-main ${
                            widgetVariant === 'openBigDrawer' ? 'p-0' : ''
                        }`}
                    >
                        {/* HEADER */}
                        <BigBookingWidgetHeader
                            propertyId={selectedPropertyId}
                        />

                        {/* MAIN CONTENT */}
                        {propertyType === constants.MULTIPROPERTY && (
                            <Multiproperty
                                enableMap={enableMap}
                                openPropertyDetails={openPropertyDetails}
                                showMap={showMap}
                            />
                        )}

                        {/* Map */}
                        {!!mapboxAccessToken && (
                            <Drawer
                                anchor={anchor}
                                open={isMapOpen}
                                height="calc(100vh - 93px)"
                                width={970}
                                top={93}
                                left={anchor === 'left' ? '69px' : ''}
                            >
                                <BookingWidgetMap
                                    height="calc(100vh - 140px)"
                                    width={970}
                                    closeMap={closeMap}
                                    markers={markers}
                                    mapboxAccessToken={mapboxAccessToken}
                                    openPropertyDetails={openPropertyDetails}
                                />
                            </Drawer>
                        )}

                        <Drawer
                            anchor={anchor}
                            open={
                                propertyType === constants.SINGLE_PROPRETY ||
                                !!selectedPropertyId
                            }
                            height="calc(100vh - 93px)"
                            width={
                                widgetVariant === 'openBigDrawer' ? 1040 : 970
                            }
                            top={93}
                            left={anchor === 'left' ? '69px' : ''}
                        >
                            <PropertyDetails
                                closeProperyDetails={closeProperyDetails}
                                property={selectedProperty}
                                showHeader={
                                    propertyType === constants.MULTIPROPERTY
                                }
                                openPropertyDetails={openPropertyDetails}
                            />
                        </Drawer>

                        {/* Room Details */}
                        <Drawer
                            anchor={anchor}
                            open={selectedRoomIndex !== null}
                            height="calc(100vh - 93px)"
                            width={
                                widgetVariant === 'openBigDrawer' ? 1040 : 970
                            }
                            top={93}
                            left={anchor === 'left' ? '69px' : ''}
                        >
                            <RoomDetails />
                        </Drawer>
                        <Drawer
                            anchor={anchor}
                            open={selectedRoomFormIndex !== null}
                            height="calc(100vh - 93px)"
                            width={
                                widgetVariant === 'openBigDrawer' ? 1040 : 970
                            }
                            top={93}
                            left={anchor === 'left' ? '69px' : ''}
                        >
                            <BookingWidgetForm />
                        </Drawer>
                        <Drawer
                            anchor={anchor}
                            open={isCartOpen !== false}
                            height="100vh"
                            width={
                                widgetVariant === 'openBigDrawer' ? 1040 : 970
                            }
                            top={0}
                            left={anchor === 'left' ? '69px' : ''}
                            className="ab-cart"
                            elevation={'calc(var(--max-z-index) - 1)'}
                        >
                            <CartForm />
                        </Drawer>
                    </div>
                </>
            </Drawer>
            {sortByClick2Call?.length > 0 && <Click2CallModal />}

            <ReviewsModal />
            {isContactUsModalOpen && <ContactUsModal />}
            {isTrackPricesModalOpen && <TrackPricesModal width={'300px'} />}
            {isCancellationAndPaymentPolicyModalOpen && (
                <CancellationAndPaymentPolicyModal />
            )}
            {isAmenitiesModalOpen && <AmenitiesModal />}
            {isAreYouSureModalOpen && <AreYouSureModal width={600} />}
            {isTermsAndConditionsModalOpen && <TermsAndConditionsModal />}
            {isShareHotelModalOpen && <ShareHotelModal />}
            {showOffer && <OfferModal />}
        </div>
    );
};

BigBookingWidgetDrawer.propTypes = {
    anchor: PropTypes.oneOf(['left', 'right', 'middle']),
    propertyType: PropTypes.string,
    widgetVariant: PropTypes.oneOf([
        constants.REGULAR,
        constants.MULTIPROPERTY,
        constants.SMALL,
        constants.OPEN_SMALL_PLUGIN,
        constants.OPEN_BIG_PLUGIN,
        constants.CLICK_2_CALL,
        constants.WIDGET_CLICK_2_CALL,
    ]),
};

export default BigBookingWidgetDrawer;
